/**
 * Local WebRTC loopback for Android voice-call audio routing.
 *
 * Android Chrome only places audio on the voice-call stream
 * (STREAM_VOICE_CALL + MODE_IN_COMMUNICATION) when it is rendered by the
 * WebRTC playout path, i.e. plays as a *remote* track of an RTCPeerConnection.
 * That classification is what gives loudspeaker output, a volume rocker bound
 * to call volume, audibility while media volume is muted, and inclusion of
 * the played audio in the echo canceller's reference signal. Web Audio and
 * plain media elements never qualify, so the call screen pipes its output
 * MediaStream through two local peer connections and plays the far end.
 * This is the same technique non-P2P calling apps use in mobile browsers.
 */

/** Handle over an established loopback: the playable stream plus teardown. */
export interface AudioLoopback {
  /** The remote (receiver-side) stream to assign to an audio element. */
  remoteStream: MediaStream;
  /** Closes both peer connections. Safe to call more than once. */
  close(): void;
}

/** Negotiation guard: a local loopback connects in milliseconds, so a stall
 * this long means the environment cannot complete it at all. */
const DEFAULT_LOOPBACK_TIMEOUT_MS = 4000;

/**
 * Feeds `stream` into a sender RTCPeerConnection, negotiates with a local
 * receiver connection, and resolves once the receiver announces the track.
 *
 * Rejects (and closes both connections) if negotiation fails or no track
 * arrives within `timeoutMs`. Callers should treat rejection as non-fatal
 * and fall back to playing `stream` directly: the audio then rides the media
 * stream instead of the voice-call stream, which is degraded but audible.
 */
export async function createAudioLoopback(
  stream: MediaStream,
  timeoutMs: number = DEFAULT_LOOPBACK_TIMEOUT_MS,
): Promise<AudioLoopback> {
  const sender = new RTCPeerConnection();
  const receiver = new RTCPeerConnection();

  const close = () => {
    sender.close();
    receiver.close();
  };

  // Local host candidates only; forward each side's candidates to the other.
  // Individual candidate failures are non-fatal (the timeout below is the
  // terminal guard), so they are swallowed rather than reported.
  sender.onicecandidate = (e) => {
    if (e.candidate) void Promise.resolve(receiver.addIceCandidate(e.candidate)).catch(() => {});
  };
  receiver.onicecandidate = (e) => {
    if (e.candidate) void Promise.resolve(sender.addIceCandidate(e.candidate)).catch(() => {});
  };

  const remoteStreamPromise = new Promise<MediaStream>((resolve) => {
    receiver.ontrack = (e) => {
      resolve(e.streams[0] ?? new MediaStream([e.track]));
    };
  });

  try {
    for (const track of stream.getAudioTracks()) {
      sender.addTrack(track, stream);
    }

    const offer = await sender.createOffer();
    await sender.setLocalDescription(offer);
    await receiver.setRemoteDescription(offer);
    const answer = await receiver.createAnswer();
    await receiver.setLocalDescription(answer);
    await sender.setRemoteDescription(answer);

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error(`WebRTC loopback timed out after ${timeoutMs}ms`)),
        timeoutMs,
      );
    });

    try {
      const remoteStream = await Promise.race([remoteStreamPromise, timeout]);
      return { remoteStream, close };
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    close();
    throw error;
  }
}
